import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { DesignSystem, getColor, getSpacing } from '../styles/designSystem';
import { CombinedItinerary } from '../api/itinerary';
import { API_BASE_URL } from '../api/tourism';

// ══════════ Props ══════════

export interface FlipCardProps {
  item: CombinedItinerary;
  variant: 'journey' | 'trek';
  index: number | string;
  isFlipped: boolean;
  isMobile: boolean;
  onFlip: (index: number | string) => void;
  onExplore: (item: CombinedItinerary) => void;
  onFrontClick?: (item: CombinedItinerary) => void;
}

// ══════════ LazyImage ══════════

const LazyImage: React.FC<{ src: string; alt: string }> = React.memo(({ src, alt }) => {
  const [loaded, setLoaded] = useState(false);

  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      decoding="async"
      onLoad={() => setLoaded(true)}
      onError={() => console.error(`[FlipCard] Image failed to load: ${src} (alt: ${alt})`)}
      style={{ opacity: loaded ? 1 : 0, transition: 'opacity 0.1s ease-in' }}
    />
  );
});

// ══════════ Styled Components ══════════

const FlipCardWrapper = styled.div<{ $isFlipped?: boolean }>`
  position: relative;
  border-radius: 12px;
  width: 280px;
  height: auto;
  aspect-ratio: 9 / 16;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  perspective: 1200px;
  cursor: pointer;
  contain: layout style paint;

  @media (max-width: 1199px) { width: 300px; }
  @media (max-width: 767px)  { width: 240px; }
  @media (max-width: 480px)  { width: min(220px, calc(100vw - 48px)); }
`;

const FlipCardInner = styled.div<{ $isFlipped?: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  transition: transform 0.7s cubic-bezier(0.4, 0, 0.2, 1);
  transform-style: preserve-3d;
  will-change: auto;
  border-radius: 12px;

  @media (min-width: 769px) {
    ${FlipCardWrapper}:hover & {
      will-change: transform;
      transform: rotateY(180deg);
    }
  }

  ${props => props.$isFlipped && `
    will-change: transform;
    transform: rotateY(180deg);
  `}
`;

const FlipCardFace = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  border-radius: 12px;
  overflow: hidden;
  -webkit-font-smoothing: antialiased;
`;

const FlipCardFront = styled(FlipCardFace)`
  z-index: 2;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 30%, transparent 55%);
    z-index: 1;
    pointer-events: none;
  }
`;

const FlipCardFrontImage = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background: linear-gradient(135deg, #a59d84 0%, #8b8370 100%);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }
`;

const FlipCardFrontContent = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  color: white;
  z-index: 3;
  text-align: left;
`;

const FlipCardFrontTitle = styled.h4`
  color: white;
  margin: 0 0 6px 0;
  font-size: 1.5rem;
  font-weight: 300;
  line-height: 1.25;
  text-shadow: 0 2px 12px rgba(0,0,0,0.8);
  font-family: ${DesignSystem.typography.fonts.display};
  letter-spacing: -0.01em;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;

  @media (max-width: 480px) {
    font-size: 1.25rem;
  }
`;

const FlipCardFrontSubtitle = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  margin: 4px 0 0 0;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.65rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  text-shadow: 0 1px 6px rgba(0,0,0,0.8);
  color: white;
`;

const FlipCardFrontHighlights = styled.div`
  margin: 5px 0 0 0;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.9rem;
  font-weight: 400;
  font-style: italic;
  color: rgba(255, 255, 255, 0.7);
  letter-spacing: 0.03em;
  text-shadow: 0 1px 6px rgba(0,0,0,0.8);
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FlipCardFrontPrice = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 3;
  background: rgba(0, 0, 0, 0.85);
  border-radius: 0 0 12px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;

  span.label {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.85);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 500;
    font-family: ${DesignSystem.typography.fonts.primary};
  }

  span.price {
    font-size: 1.1rem;
    font-weight: 700;
    color: #CAB96E;
    letter-spacing: -0.02em;
    font-family: ${DesignSystem.typography.fonts.display};
  }

  @media (max-width: 480px) {
    padding: 8px 12px;

    span.label {
      font-size: 0.65rem;
    }
    span.price {
      font-size: 0.95rem;
    }
  }
`;

// ══════════ Back Face ══════════

const FlipCardBack = styled(FlipCardFace)`
  transform: rotateY(180deg);
  background: ${getColor('neutral.white')};
  z-index: 1;
  display: flex;
  flex-direction: column;
  padding: 28px 22px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
  border: 1px solid ${getColor('primary.200')};
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar { display: none; }

  @media (max-width: 767px) {
    padding: 20px 16px;
  }
  @media (max-width: 480px) {
    padding: 16px 14px;
  }
`;



const FlipCardBackDescription = styled.p`
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.82rem;
  color: ${getColor('primary.900')};
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex-shrink: 0;

  @media (max-width: 480px) {
    -webkit-line-clamp: 4;
    font-size: 0.78rem;
  }
`;

const FlipCardTrekDescription = styled(FlipCardBackDescription)`
  -webkit-line-clamp: 7;
  margin: 0 0 8px 0;

  @media (max-width: 767px) {
    -webkit-line-clamp: 5;
  }
  @media (max-width: 480px) {
    -webkit-line-clamp: 4;
    margin: 0 0 4px 0;
  }
`;

const TrekBottomSection = styled.div`
  margin-top: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const MiniAltitudeLabel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;

  span {
    font-family: ${DesignSystem.typography.fonts.primary};
    font-size: 0.52rem;
    color: ${getColor('primary.800')};
    text-transform: uppercase;
    letter-spacing: 0.06em;
    font-weight: 600;
  }

  span.peak {
    color: ${getColor('primary.900')};
    font-weight: 700;
  }
`;

const MiniAltitudeChart = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 3px;
  height: 36px;
  padding: 4px 6px;
  background: ${getColor('primary.50')};
  border-radius: 6px;
  border: 1px solid ${getColor('primary.200')};
  margin-bottom: 4px;

  @media (max-width: 480px) {
    height: 28px;
    padding: 3px 4px;
  }
`;

const MiniAltitudeBar = styled.div<{ $heightPct: number; $isTopPeak?: boolean }>`
  flex: 1;
  height: ${props => Math.max(props.$heightPct, 8)}%;
  background: ${props => props.$isTopPeak ? '#CAB96E' : '#DFD5A8'};
  border: 1px solid ${props => props.$isTopPeak ? '#CAB96E' : '#D4CA96'};
  border-bottom: none;
  border-radius: 2px 2px 0 0;
  transition: height 0.3s ease;
`;

const MiniAltitudeDayAxis = styled.div`
  display: flex;
  justify-content: space-between;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.42rem;
  color: ${getColor('primary.700')};
  margin-bottom: 0px;
  padding: 0 2px;
`;

const FlipCardBackFact = styled.p`
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.82rem;
  color: ${getColor('primary.900')};
  line-height: 1.6;
  margin: 0 0 8px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
  padding-left: 16px;
  position: relative;

  &::before {
    content: '✦';
    position: absolute;
    left: 0;
    color: ${getColor('accent.600')};
    font-size: 0.7rem;
  }
`;

const FlipCardBackMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: auto;
  padding-top: 13px;
  border-top: 1px solid ${getColor('primary.200')};

  @media (max-width: 767px) {
    gap: 7px;
    padding-top: 8px;
  }
  @media (max-width: 480px) {
    gap: 5px;
    padding-top: 6px;
  }
`;

const FlipCardMetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FlipCardMetaIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: transparent;
  color: #CAB96E;
  font-size: 0.64rem;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 22px;
    height: 22px;
  }
`;

const FlipCardMetaLabel = styled.span`
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.58rem;
  color: ${getColor('primary.700')};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 0.52rem;
  }
`;

const FlipCardMetaValue = styled.span`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 0.8rem;
  color: ${getColor('primary.900')};
  font-weight: 600;
  letter-spacing: -0.01em;

  @media (max-width: 480px) {
    font-size: 0.72rem;
  }
`;

const FlipCardPricingTiers = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 6px;
  width: 100%;
  margin: 4px 0;
`;

const FlipCardPriceTier = styled.div<{ $highlighted?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 4px 2px;
  border-radius: 6px;
  background: white;
  border: 1px solid #CAB96E;
  transition: all 0.2s ease;
`;

const FlipCardTierLabel = styled.span`
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.58rem;
  color: ${getColor('primary.800')};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 600;

  @media (max-width: 480px) {
    font-size: 0.5rem;
  }
`;

const FlipCardTierPrice = styled.span`
  font-family: ${DesignSystem.typography.fonts.display};
  font-size: 0.8rem;
  color: ${getColor('primary.900')};
  font-weight: 700;
  letter-spacing: -0.01em;

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

const FlipCardIncludesTag = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.55rem;
  color: ${getColor('primary.800')};
  font-weight: 600;
  letter-spacing: 0.04em;

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
    color: ${getColor('primary.700')};
  }
`;

const FlipCardExploreButton = styled.button`
  margin-top: auto;
  flex-shrink: 0;
  width: 100%;
  padding: 8px 16px;
  background: white;
  border: 1px solid #E0DDD0;
  color: ${getColor('primary.800')};
  font-family: 'DM Sans', sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  border-radius: 50px;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(101, 90, 71, 0.2);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: #CAB96E;
    color: white;
    border-color: #CAB96E;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(202, 185, 110, 0.35);
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    padding: 7px 12px;
    font-size: 0.65rem;
  }
`;

// ══════════ Difficulty Badge (top-right, trek only) ══════════

const FlipCardDifficultyBadge = styled.div`
  position: absolute;
  bottom: 92px;
  right: 12px;
  z-index: 4;
  padding: 6px 14px;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.73rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #CAB96E;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50px;
  white-space: nowrap;

  @media (max-width: 480px) {
    bottom: 82px;
    padding: 4px 10px;
    font-size: 0.65rem;
  }
`;

// ══════════ Front Info Bar (above price) ══════════

const FlipCardInfoBar = styled.div`
  position: absolute;
  bottom: 46px;
  left: 0;
  right: 0;
  z-index: 3;
  background: rgba(0, 0, 0, 0.05);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 10px;
  font-family: ${DesignSystem.typography.fonts.primary};
  font-size: 0.73rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: white;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  .separator {
    margin: 0 4px;
    color: rgba(255, 255, 255, 0.35);
    font-weight: 300;
  }

  .gold {
    color: #CAB96E;
  }

  @media (max-width: 480px) {
    font-size: 0.65rem;
    padding: 8px 8px;
    bottom: 42px;
    letter-spacing: 0.04em;
  }
`;

// ══════════ Skeleton ══════════

export const FlipCardSkeleton = styled.div`
  width: 280px;
  aspect-ratio: 9 / 16;
  flex-shrink: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, #a59d84 0%, #8b8370 100%);
  animation: skeletonPulse 1.5s ease-in-out infinite;

  @keyframes skeletonPulse {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
  }

  @media (max-width: 1199px) { width: 300px; }
  @media (max-width: 767px)  { width: 240px; }
  @media (max-width: 480px)  { width: min(220px, calc(100vw - 48px)); }
`;

// ══════════ SVG Icons ══════════

const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
    <circle cx="12" cy="10" r="3"/>
  </svg>
);

const DistanceIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
    <line x1="16" y1="2" x2="16" y2="6"/>
    <line x1="8" y1="2" x2="8" y2="6"/>
    <line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

const PriceTagIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
    <line x1="7" y1="7" x2="7.01" y2="7"/>
  </svg>
);

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/>
    <polyline points="22 4 12 14.01 9 11.01"/>
  </svg>
);

const DifficultyIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 22h20L12 2z"/></svg>
);

const CrosshairIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M2 12h20M12 2v20"/></svg>
);

const AltitudeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v8l4 4"/><circle cx="12" cy="14" r="8"/></svg>
);

// ══════════ Journey Back Face ══════════

const JourneyBackFace: React.FC<{ item: CombinedItinerary; onExplore: (item: CombinedItinerary) => void }> = ({ item, onExplore }) => (
  <FlipCardBack>
    <FlipCardBackDescription>
      {item.cardDescription || item.shortDescription || item.description?.substring(0, 120) + '...'}
    </FlipCardBackDescription>

    <FlipCardBackMeta>
      {item.state && item.state.length > 0 && (
        <FlipCardMetaRow>
          <FlipCardMetaIcon><LocationIcon /></FlipCardMetaIcon>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <FlipCardMetaLabel>State</FlipCardMetaLabel>
            <FlipCardMetaValue>{item.state.join(', ')}</FlipCardMetaValue>
          </div>
        </FlipCardMetaRow>
      )}

      {item.totalDistance && (
        <FlipCardMetaRow>
          <FlipCardMetaIcon><DistanceIcon /></FlipCardMetaIcon>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <FlipCardMetaLabel>Total Distance</FlipCardMetaLabel>
            <FlipCardMetaValue>{item.totalDistance}</FlipCardMetaValue>
          </div>
        </FlipCardMetaRow>
      )}

      <FlipCardMetaRow>
        <FlipCardMetaIcon><CalendarIcon /></FlipCardMetaIcon>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <FlipCardMetaLabel>Duration</FlipCardMetaLabel>
          <FlipCardMetaValue>
            {(item as any).totalDays ? `${(item as any).totalDays} Days` : item.packageDuration}
          </FlipCardMetaValue>
        </div>
      </FlipCardMetaRow>

      <FlipCardMetaRow>
        <FlipCardMetaIcon><PriceTagIcon /></FlipCardMetaIcon>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <FlipCardMetaLabel>Price Per Person</FlipCardMetaLabel>
        </div>
      </FlipCardMetaRow>

      <FlipCardPricingTiers>
        <FlipCardPriceTier>
          <FlipCardTierLabel>Standard</FlipCardTierLabel>
          <FlipCardTierPrice>{item.pricePerPerson?.standard || item.costPerPerson || '—'}</FlipCardTierPrice>
        </FlipCardPriceTier>
        <FlipCardPriceTier $highlighted>
          <FlipCardTierLabel>Deluxe</FlipCardTierLabel>
          <FlipCardTierPrice>{item.pricePerPerson?.deluxe || '—'}</FlipCardTierPrice>
        </FlipCardPriceTier>
        <FlipCardPriceTier>
          <FlipCardTierLabel>Premium</FlipCardTierLabel>
          <FlipCardTierPrice>{item.pricePerPerson?.premium || '—'}</FlipCardTierPrice>
        </FlipCardPriceTier>
      </FlipCardPricingTiers>

      <FlipCardIncludesTag>
        <CheckIcon />
        INCLUDES  Car + Hotel (MAP)
      </FlipCardIncludesTag>
    </FlipCardBackMeta>

    <FlipCardExploreButton
      onClick={(e) => {
        e.stopPropagation();
        onExplore(item);
      }}
    >
      Explore Journey →
    </FlipCardExploreButton>
  </FlipCardBack>
);

// ══════════ Trek Back Face ══════════

const TrekBackFace: React.FC<{ item: CombinedItinerary; onExplore: (item: CombinedItinerary) => void }> = ({ item, onExplore }) => {
  const altitudeProfile = useMemo(() => {
    if (!item.days || item.days.length === 0) return null;
    const altitudes = item.days
      .map((d: any) => d.altitude?.end ?? d.altitude?.peak ?? null)
      .filter((a: number | null): a is number => a !== null);
    if (altitudes.length < 2) return null;
    const maxAlt = Math.max(...altitudes);
    if (maxAlt === 0) return null;
    const sorted = altitudes
      .map((a, i) => ({ alt: a, idx: i }))
      .sort((a, b) => b.alt - a.alt);
    const topTwoIndices = new Set(sorted.slice(0, 2).map(s => s.idx));
    return {
      bars: altitudes.map(a => ({ heightPct: (a / maxAlt) * 100, altitude: a })),
      peak: maxAlt,
      totalDays: altitudes.length,
      topTwoIndices,
    };
  }, [item.days]);

  return (
    <FlipCardBack>
      <FlipCardTrekDescription>
        {item.cardDescription || (item as any).overview || item.description || 'An unforgettable trek through pristine Himalayan landscapes.'}
      </FlipCardTrekDescription>

      <TrekBottomSection>
        {altitudeProfile && (
          <>
            <MiniAltitudeLabel>
              <span>Elevation Profile</span>
              <span className="peak">Peak: {altitudeProfile.peak.toLocaleString()}m</span>
            </MiniAltitudeLabel>
            <MiniAltitudeChart>
              {altitudeProfile.bars.map((bar, i) => (
                <MiniAltitudeBar key={i} $heightPct={bar.heightPct} $isTopPeak={altitudeProfile.topTwoIndices.has(i)} />
              ))}
            </MiniAltitudeChart>
            <MiniAltitudeDayAxis>
              <span>D1</span>
              <span>D{altitudeProfile.totalDays}</span>
            </MiniAltitudeDayAxis>
          </>
        )}

        <FlipCardBackMeta style={{ marginTop: 0 }}>
        <FlipCardMetaRow>
          <FlipCardMetaIcon><DifficultyIcon /></FlipCardMetaIcon>
          <div>
            <FlipCardMetaLabel>Difficulty</FlipCardMetaLabel>
            <FlipCardMetaValue>{item.difficulty || 'Moderate'}</FlipCardMetaValue>
          </div>
        </FlipCardMetaRow>

        <FlipCardMetaRow>
          <FlipCardMetaIcon><CrosshairIcon /></FlipCardMetaIcon>
          <div>
            <FlipCardMetaLabel>Distance</FlipCardMetaLabel>
            <FlipCardMetaValue>{item.totalDistance ? `${item.totalDistance} km` : 'Varies'}</FlipCardMetaValue>
          </div>
        </FlipCardMetaRow>

        <FlipCardMetaRow>
          <FlipCardMetaIcon><AltitudeIcon /></FlipCardMetaIcon>
          <div>
            <FlipCardMetaLabel>Max Altitude</FlipCardMetaLabel>
            <FlipCardMetaValue>{item.maxAltitude ? `${item.maxAltitude}m` : 'Varies'}</FlipCardMetaValue>
          </div>
        </FlipCardMetaRow>

        <FlipCardMetaRow>
          <FlipCardMetaIcon><DistanceIcon /></FlipCardMetaIcon>
          <div>
            <FlipCardMetaLabel>Elevation Gain</FlipCardMetaLabel>
            <FlipCardMetaValue>{item.elevationGain ? `${item.elevationGain}m` : 'Varies'}</FlipCardMetaValue>
          </div>
        </FlipCardMetaRow>

        <FlipCardMetaRow>
          <FlipCardMetaIcon><CalendarIcon /></FlipCardMetaIcon>
          <div>
            <FlipCardMetaLabel>Best Season</FlipCardMetaLabel>
            <FlipCardMetaValue>{item.bestTimeToVisit || (item as any).bestSeason?.[0] || 'Summer'}</FlipCardMetaValue>
          </div>
        </FlipCardMetaRow>
        </FlipCardBackMeta>

        <FlipCardExploreButton
          onClick={(e) => {
            e.stopPropagation();
            onExplore(item);
          }}
        >
          Explore Trek →
        </FlipCardExploreButton>
      </TrekBottomSection>
    </FlipCardBack>
  );
};

// ══════════ Main FlipCard Component ══════════

const FlipCard: React.FC<FlipCardProps> = ({
  item,
  variant,
  index,
  isFlipped,
  isMobile,
  onFlip,
  onExplore,
  onFrontClick,
}) => {
  const isJourney = variant === 'journey';
  const title = isJourney
    ? (item.title || item.name)
    : (item.trekName || item.name);

  // Handle cardImage: backend returns relative paths like "itineraries/journey/4D_manali.webp"
  // Prepend API_BASE_URL + /api/images/ to construct full endpoint URL
  const imageUrl = item.cardImage
    ? `${API_BASE_URL}/api/images/${item.cardImage}`
    : null;

  if (!imageUrl) {
    console.error(`[FlipCard] Missing cardImage for: "${title}" (id: ${item.id})`);
  }

  const frontPrice = isJourney
    ? `${item.pricePerPerson?.standard || item.costPerPerson || 'On Request'} / person`
    : 'On Request / person';

  const durationLabel = isJourney
    ? item.packageDuration
    : `${item.duration} Days`;

  const seasonLabel = useMemo(() => {
    let raw = item.bestTimeToVisit;
    if (!raw) return null;
    // Normalize: could be object { primarySeason: "..." } or non-string
    if (typeof raw === 'object' && raw !== null) {
      raw = (raw as any).primarySeason || '';
    }
    if (typeof raw !== 'string' || !raw) return null;
    const monthMap: Record<string, string> = {
      january: 'Jan', february: 'Feb', march: 'Mar', april: 'Apr',
      may: 'May', june: 'Jun', july: 'Jul', august: 'Aug',
      september: 'Sep', october: 'Oct', november: 'Nov', december: 'Dec',
    };
    const parts = raw.split(/\s*[-–,]\s*/);
    const abbrevs = parts.map((p: string) => monthMap[p.trim().toLowerCase()] || p.trim());
    return abbrevs.length === 2 ? `${abbrevs[0]}–${abbrevs[1]}` : abbrevs.join(', ');
  }, [item.bestTimeToVisit]);

  return (
    <FlipCardWrapper
      $isFlipped={isFlipped}
      onClick={() => isMobile && onFlip(index)}
    >
      <FlipCardInner $isFlipped={isFlipped}>
        {/* ═══ FRONT FACE ═══ */}
        <FlipCardFront
          onClick={(e) => {
            if (isMobile) return;
            if (onFrontClick) {
              e.stopPropagation();
              onFrontClick(item);
            }
          }}
        >
          <FlipCardFrontImage>
            {imageUrl ? (
              <LazyImage src={imageUrl} alt={title || 'Card'} />
            ) : null}
          </FlipCardFrontImage>
          <FlipCardFrontContent>
            <FlipCardFrontTitle>{title}</FlipCardFrontTitle>
            {(() => {
              const subtitle = isJourney ? item.state?.[0] : item.region;
              return subtitle ? (
                <FlipCardFrontSubtitle>
                  <span>{subtitle}</span>
                </FlipCardFrontSubtitle>
              ) : null;
            })()}
            {(() => {
              const highlights = item.trip_highlights || item.highlights || [];
              return highlights.length > 0 ? (
                <FlipCardFrontHighlights>
                  {highlights.slice(0, 5).join(', ')}{highlights.length > 5 ? ' …' : ''}
                </FlipCardFrontHighlights>
              ) : null;
            })()}
          </FlipCardFrontContent>

          <FlipCardInfoBar>
            <span>{item.tour_type === 'bike' ? 'Bike' : item.tour_type === 'trek' ? 'Trek' : 'Car'}</span>
            <span className="separator">·</span>
            <span>{durationLabel}</span>
            {seasonLabel && (
              <><span className="separator">·</span><span>{seasonLabel}</span></>
            )}
          </FlipCardInfoBar>

          <FlipCardFrontPrice>
            <span className="label">Starting from</span>
            <span className="price">{frontPrice}</span>
          </FlipCardFrontPrice>
        </FlipCardFront>

        {/* ═══ BACK FACE ═══ */}
        {isJourney ? (
          <JourneyBackFace item={item} onExplore={onExplore} />
        ) : (
          <TrekBackFace item={item} onExplore={onExplore} />
        )}
      </FlipCardInner>
    </FlipCardWrapper>
  );
};

export default FlipCard;

// ══════════ Re-exports for TrekBackFace usage in other components ══════════
export { TrekBackFace };
export {
  FlipCardBack,
  FlipCardTrekDescription,
  TrekBottomSection,
  MiniAltitudeLabel,
  MiniAltitudeChart,
  MiniAltitudeBar,
  MiniAltitudeDayAxis,
  FlipCardBackMeta,
  FlipCardMetaRow,
  FlipCardMetaIcon,
  FlipCardMetaLabel,
  FlipCardMetaValue,
  FlipCardExploreButton,
};
